import React, { useEffect, useRef, useState } from "react";
import { useCoAgent, useCopilotAction } from "@copilotkit/react-core";
import { Role } from "@copilotkit/runtime-client-gql";
import { truncate } from "lodash";
import Button from "components/common/Button";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { chatActions } from "redux/Ldb/actions";
import ReactMarkdown from "react-markdown";
import { AgentState, MediaAttachments } from "types/salesTargetType";
import SalesTargetThinkingState from "./ChatComponent/AgentThinkingState";
import CustomToast from "components/common/CustomToast";
import toast from "react-hot-toast";
import { displayFileIcon, getFromStorage } from "helpers";
import { getFileTypeFromUrl } from "helpers/getFileTypeFromUrl";
import { firebaseService } from "services/firebaseService";
import Loader from "components/common/Loader";
import CustomEditor from "./Editor/Editor";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLangGraphInterrupt, useCopilotChatHeadless_c } from "@copilotkit/react-core";
import { v4 as uuidv4 } from "uuid";
import { toolRenderers } from "./ToolRenderer";
import ChatHistory from "./ChatHistory";
import { useMergedSearchParams } from "helpers/mergeSearchParams";
import ModalContainer from "components/common/ModalContainer";
import useGetTaskList from "hooks/workforceHooks/useGetTaskList";
import { flowActions } from "redux/Ldb/actions/flowActions";
import { convertBytes } from "helpers/displayFileSize";

const ChatUIComponent = ({
    open,
    setOpen,
    mode,
    pageType,
}: {
    open?: boolean;
    setOpen?: (open: boolean) => void;
    mode?: string;
    pageType?: string;
}) => {
    const navigate = useNavigate();
    const userProfile = getFromStorage("ally-user");
    const { state: locationState } = useLocation();
    const {
        content: contentMessageInUrl,
        filesAttached: filesAttachedInUrl,
        selectedIds: selectedIdsInUrl,
    } = locationState || {};
    const dispatch = useAppDispatch();
    const { updateSearchParams } = useMergedSearchParams();
    const { threadId: threadIdInUrl } = useParams();
    const chatRef = useRef<HTMLDivElement>(null);
    const [message, setMessage] = useState<string>("");
    const [contentMessage, setContentMessage] = useState<string>("");
    const [previousChatLayout, setPreviousChatLayout] = useState<boolean>(false);
    const [loadingAttachName, setLoadingAttachName] = useState<string>("");
    const [filesAttached, setFilesAttached] = useState<{ [key: string]: any }[]>([]);
    const [selectedIds, setSelectedIds] = useState<{ [key: string]: any }[]>([]);
    const [currentSelectedIds, setCurrentSelectedIds] = useState<{ [key: string]: any }[]>([]);
    const [currentChat, setCurrentChat] = useState<string>("");
    const [uploadingDoc, setUploadingDoc] = useState<boolean>(false);
    const [expandedId, setExpandedId] = useState<string>("");
    const [threadId, setThreadId] = useState<string>("");
    const [canvasRenderData, setCanvasRenderData] = useState<Record<string, any> | null>(null);
    const [stopModal, setStopModal] = useState<boolean>(false);
    const [cantHandleTaskModal, setCantHandleTaskModal] = useState<boolean>(false);
    const [additionalInfoModal, setAdditionalInfoModal] = useState<boolean>(false);
    const [userFeedbackMsg, setUserFeedbackMsg] = useState<string>("");
    const [feedbackDetails, setFeedbackDetails] = useState<string>("");
    const [media, setMedia] = useState<any[]>([]);
    const [copied, setCopied] = useState<boolean>(false);
    const { fetchChatThreadSuccess } = useAppSelector((state) => state.chat);
    const { createConversationSuccess } = useAppSelector((state) => state.chat);
    const { sendingFlowFeedback, sentFlowFeedbackSuccess } = useAppSelector((state) => state.flow);
    const [isFileUrl, setIsFileUrl] = useState<boolean>(false);

    const { setUpdatedPage } = useGetTaskList();
    // @ts-ignore
    useCopilotAction({
        name: "GenerateSalesTargetForecast",
        description: "Generate the sales target forecast for a list of inventory items.",
        available: "disabled",
        render: ({ args, status, result }) => {
            if (status !== "complete") {
                return <div>Loading...</div>;
            }

            return <></>;
        },
    });

    const {
        messages,
        sendMessage,
        interrupt,
        reloadMessages,
        reset,
        isLoading,
        setMessages,
        stopGeneration,
    } = useCopilotChatHeadless_c();

    // console.log(messages, "messages");

    const { nodeName, running, state, setState } = useCoAgent<AgentState>({
        name: "synth-gpt",
        config: {
            subgraphs: true,
            configurable: {
                ldbId: userProfile?.user?.parentId || userProfile?.user?._id,
                thread_id: threadIdInUrl || threadId || "",
            },
        },
    });

    const handleSendFlowFeedback = () => {
        const body = {
            threadId: threadId,
            feedback: feedbackDetails + " " + userFeedbackMsg,
        };
        dispatch(flowActions.sendFlowFeedback(body));
    };

    useEffect(() => {
        if (Boolean(sentFlowFeedbackSuccess)) {
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title={"Shared with Synth product team"}
                    message={
                        "Got it. I've shared this with our product team for review. Thanks for helping us improve—your input makes me smarter."
                    }
                    type={"success"}
                />
            ));
            dispatch(flowActions.resetFlowFeedback());
            setAdditionalInfoModal(false);
            setUserFeedbackMsg("");
        }
    }, [dispatch, sentFlowFeedbackSuccess]);

    const handleUpload = (fileName, file, size) => {
        setUploadingDoc(true);
        firebaseService.uploadFile(
            `synthally/${process.env.REACT_APP_STAGE}/flow-docs/${fileName}`,
            file,
            (url) => {
                setFilesAttached((prevFiles) => [
                    ...prevFiles,
                    {
                        fileName: fileName,
                        path: url,
                        size: size,
                    },
                ]);
                setUploadingDoc(false);
                setLoadingAttachName("");
            }
        );
    };

    const deleteAttachment = (idx) => {
        const copyData = [...filesAttached];

        const imagePath = decodeURIComponent(
            copyData[idx]?.path?.split("/o/")?.[1]?.split("?alt")?.[0]
        );

        firebaseService.removeFile(imagePath, () => {
            copyData?.splice(idx, 1);
            setFilesAttached(copyData);
        });
    };

    useEffect(() => {
        setState((prev) => ({
            ...prev,
            productContext:
                selectedIds?.length > 0
                    ? [
                          ...(prev.productContext || []),
                          ...selectedIds?.map((id) => ({
                              productName: id?.productName,
                              sku: id?.sku,
                              inventoryId: id?._id,
                          })),
                      ]
                    : prev.productContext || [],
        }));
    }, [selectedIds]);

    useEffect(() => {
        if (state?.media) {
            setMedia(state.media);
        }
    }, [state?.media]);

    const handleCreateConversation = (content?: boolean) => {
        //disable start for new chat if there is a chat open
        if (messages?.length > 0 || content) {
            const body = {
                title: "New Chat",
                receiverType: "synth-gpt",
                conversationType: "sales_target_forecast",
            };
            dispatch(chatActions.createConversation(body));
        }
    };

    useEffect(() => {
        if (contentMessageInUrl) {
            setThreadId(threadIdInUrl);
            setSelectedIds(selectedIdsInUrl?.length > 0 ? selectedIdsInUrl : []);
            handleSendMessage(contentMessageInUrl, filesAttachedInUrl);
        }
    }, [contentMessageInUrl, filesAttachedInUrl, threadIdInUrl]);

    useEffect(() => {
        if (copied) {
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        }
    }, [copied]);

    const handleSendMessage = async (content: string, filesAttached?: any[]) => {
        if (isLoading) {
            return;
        }

        if (!threadId && !threadIdInUrl) {
            setContentMessage(content);
            handleCreateConversation(true);
        } else if (content.trim()) {
            handleAddAttachmentToState(filesAttached);
            await sendMessage({
                id: uuidv4(),
                role: Role.User,
                content,
            });
            setMessage("");
        }
    };

    const handleAddAttachmentToState = (filesToAdd?: any[]) => {
        if (filesToAdd?.length > 0) {
            setState((prev) => ({
                ...prev,
                media:
                    filesToAdd?.length > 0
                        ? [...(prev.media || []), ...(filesToAdd as MediaAttachments[])]
                        : prev.media || [],
            }));
        } else if (filesAttached?.length > 0) {
            setState((prev) => ({
                ...prev,
                media:
                    filesAttached?.length > 0
                        ? [...(prev.media || []), ...(filesAttached as MediaAttachments[])]
                        : prev.media || [],
            }));
        }
        setFilesAttached([]);
    };

    useEffect(() => {
        if (Boolean(createConversationSuccess)) {
            reset();
            const newThreadId = createConversationSuccess?.conversation?.threadId;
            dispatch(chatActions.fetchChatThread(newThreadId));
            setUpdatedPage(1);
            // for salesTarget UI
            if (contentMessage) {
                setTimeout(() => {
                    handleSendMessageNewThread(contentMessage, fetchChatThreadSuccess);
                }, 2000);
            }
            dispatch(chatActions.resetCreateConversationSuccess());
        }
    }, [createConversationSuccess]);

    const handleSendMessageNewThread = async (content: string, threadId: string) => {
        if (pageType === "flow") {
            navigate(`/dashboard/home/${threadId}`);
        }
        handleAddAttachmentToState();
        await sendMessage({
            id: uuidv4(),
            role: Role.User,
            content,
        });
        setMessage("");
        setContentMessage("");
    };

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTo({
                top: chatRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [messages]);

    useEffect(() => {
        if (fetchChatThreadSuccess) {
            setThreadId(fetchChatThreadSuccess);
        }
    }, [fetchChatThreadSuccess]);

    useEffect(() => {
        if (threadIdInUrl && !contentMessageInUrl) {
            dispatch(chatActions.fetchChatThread(threadIdInUrl));
            setThreadId(threadIdInUrl);
        }
    }, [threadIdInUrl, contentMessageInUrl]);

    useEffect(() => {
        if (expandedId) {
            const details = messages?.find((message) => message.id === expandedId);
            const resMess = JSON.parse(details?.content);
            const toolName = "toolName" in details && details?.toolName;
            setCanvasRenderData({
                resultMessage: resMess,
                toolName: toolName,
            });
        } else {
            setCanvasRenderData(null);
        }
    }, [expandedId]);

    useEffect(() => {
        if (!isLoading) {
            setStopModal(false);
        }
    }, [isLoading]);

    

    useLangGraphInterrupt<string>({
        render: ({ event, resolve }) => {
            let value: unknown = "";
            let type: unknown = "";
            const eventValue = event?.value;
            if (eventValue !== null && typeof eventValue === "object") {
                const contentProperty = eventValue as { content?: unknown; type?: unknown };

                if (contentProperty !== undefined) {
                    value = contentProperty?.content;
                    type = contentProperty?.type;
                }
            }

            return (
                type === "deleteOrders" && (
                    <div className="bg-white mb-3 w-full max-w-[536px] border p-3.5 text-sm border-slate-200 rounded-xl">
                        <p className="text-g-75">Delete this sales order?</p>
                        <p className="w-fit text-slate-500">{value as string}</p>
                        <div className="mt-2 flex items-center gap-2">
                            <Button
                                btnText={`Yes, Delete `}
                                btnClassname="!bg-[#E4281D] !w-fit !text-[13px] !whitespace-nowrap !px-2 !py-1 rounded-[5px] !text-white flex-shrink-0"
                                icon={<i className="ri-check-fill text-white "></i>}
                                type="button"
                                onClick={() => resolve("yes")}
                            />
                            <Button
                                btnText={`No, cancel `}
                                btnClassname="!bg-white !w-fit !text-[13px] !whitespace-nowrap !px-2 !py-1 rounded-[5px] !text-g-75 flex-shrink-0 border"
                                icon={<i className="ri-close-fill text-[#E4281D] "></i>}
                                type="button"
                                onClick={() => resolve("no")}
                            />
                        </div>
                    </div>
                )
            );
        },
    });

    const lastUserIndex = [...messages].reverse().findIndex((m) => m.role === "user");
    const actualUserIndex = lastUserIndex === -1 ? -1 : messages.length - 1 - lastUserIndex;
    const messagesAfterLastUser = actualUserIndex === -1 ? [] : messages.slice(actualUserIndex + 1);
    const Messages = () => {
        return (
            <div className={`${expandedId ? "p-0" : "py-2 px-6"} space-y-2 `}>
                <div
                    className={` overflow-y-auto z-0  overflow-x-clip space-y-2 flex flex-col justify-between ${
                        expandedId &&
                        pageType !== "salesAgent" &&
                        (filesAttached?.length > 0 || uploadingDoc)
                            ? "h-[calc(100vh-460px)]"
                            : expandedId && pageType !== "salesAgent"
                            ? "h-[calc(100vh-390px)]"
                            : !expandedId &&
                              pageType !== "salesAgent" &&
                              filesAttached?.length === 0 &&
                              !uploadingDoc &&
                              mode === "flow"
                            ? "h-[calc(100vh-410px)]"
                            : !expandedId &&
                              pageType !== "salesAgent" &&
                              filesAttached?.length === 0 &&
                              !uploadingDoc &&
                              mode !== "flow"
                            ? "h-[calc(100vh-410px)]"
                            : !expandedId &&
                              pageType !== "salesAgent" &&
                              (filesAttached?.length > 0 || uploadingDoc) &&
                              mode === "flow"
                            ? "h-[calc(100vh-480px)]"
                            : !expandedId &&
                              pageType !== "salesAgent" &&
                              (filesAttached?.length > 0 || uploadingDoc)
                            ? "h-[calc(100vh-480px)]"
                            : filesAttached?.length > 0 || uploadingDoc
                            ? "h-[430px]"
                            : "h-[500px]"
                    }`}
                    ref={chatRef}
                >
                    <div className="">
                        {pageType === "flow" && !expandedId ? (
                            <div className={`flex items-center gap-2 justify-start mb-3`}>
                                <p
                                    className={`w-fit max-w-[50%] rounded-xl p-3.5 text-sm
                                             bg-[#D0F4ED59] text-slate-500  `}
                                >
                                    Welcome! I'm here to help you manage your orders, inventory, and
                                    planning. Ask me anything to get started.
                                </p>
                            </div>
                        ) : (
                            !expandedId && (
                                <div className="">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-4 h-4 rounded-full flex items-center justify-center bg-g-75">
                                            <img
                                                src="https://storage.googleapis.com/heysynth_files/synth_webapp/Group%201.svg"
                                                alt="synth logo"
                                                className="w-3 h-3"
                                            />
                                        </div>
                                        <p className="text-sm text-g-75">Synth</p>
                                    </div>
                                    <div className={`flex items-center gap-2 justify-start mb-2`}>
                                        <p
                                            className={`w-fit max-w-[80%] rounded-xl p-3.5 text-sm
                                             bg-[#D0F4ED59] text-g-75  `}
                                        >
                                            Hi there 👋 I'm Synth, your AI Operations Assistant.
                                            Tell me your sales goals, and I'll turn them into a
                                            forecast and target. What are we aiming for today?
                                        </p>
                                    </div>
                                </div>
                            )
                        )}

                        {messages?.map((message, idx) => {
                            const currentUser = messages[idx]?.role;

                            const previousUser = messages[idx - 1]?.role;
                            const isStartOfAssistantThread =
                                previousUser === "user" && messages[idx]?.content;

                            const messageContent =
                                "content" in message &&
                                !message?.content?.startsWith("runSummary:") &&
                                (message.role === "assistant" || message.role === "user") &&
                                message.content;

                            const messageRole = "content" in message && message.role;

                            const messageId = message.id;

                            const errorId =
                                "content" in message &&
                                (message.content as string)?.startsWith("An error") &&
                                messageRole === "assistant" &&
                                message.id;

                            let resultMessage: any = null;

                            const toolName = "toolName" in message && message?.toolName;

                            const type = "type" in message && message.type;
                            if (
                                message &&
                                message.role === "tool" &&
                                "content" in message &&
                                typeof message.content === "string" &&
                                "toolName" in message
                            ) {
                                try {
                                    resultMessage = JSON.parse(message.content);
                                    // console.log(resultMessage, "resultMessage", toolName);
                                } catch (e) {}
                            }
                            const Renderer = toolRenderers[toolName];

                            if (messageRole !== "user") {
                                return (
                                    <div className="">
                                        <div className="mb-2" key={idx}>
                                            {isStartOfAssistantThread && (
                                                <>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <div className="w-4 h-4 rounded-full flex items-center justify-center bg-g-75">
                                                            <img
                                                                src="https://storage.googleapis.com/heysynth_files/synth_webapp/Group%201.svg"
                                                                alt="synth logo"
                                                                className="w-3 h-3"
                                                            />
                                                        </div>
                                                        <p className="text-sm text-g-75">Synth</p>
                                                    </div>
                                                </>
                                            )}
                                            {messageId?.startsWith("actionGuardNode") ? (
                                                <div className="bg-white mb-3 w-full max-w-[536px] border p-3.5 text-sm border-slate-200 rounded-xl shadow-[0px_4px_8px_-4px_rgba(16,24,40,0.03)]">
                                                    <p className="text-g-75">{messageContent}</p>
                                                    <p className="text-slate-500 text-sm">
                                                        Would you like me to share this with our
                                                        product team so we can improve?
                                                    </p>
                                                    <Button
                                                        btnText={"Yes"}
                                                        btnClassname={
                                                            "!py-1.5 !w-fit !bg-[#0AAD0A] !text-white !rounded-[4px]"
                                                        }
                                                        onClick={() => {
                                                            setAdditionalInfoModal(true);
                                                            setFeedbackDetails(messageContent);
                                                        }}
                                                        icon={
                                                            <i className="ri-check-fill text-white"></i>
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                messageContent?.length > 0 && (
                                                    <div
                                                        className={`flex items-center gap-2 justify-start`}
                                                    >
                                                        <div
                                                            className={`w-fit ${
                                                                expandedId
                                                                    ? "max-w-[100%]"
                                                                    : "max-w-[80%]"
                                                            } rounded-xl p-3.5 text-sm
                                              ${
                                                  messageContent?.startsWith("An error")
                                                      ? "bg-[#FEEBE6] text-[#D43318]"
                                                      : "bg-[#D0F4ED59] text-slate-500"
                                              } `}
                                                        >
                                                            <div className="markdown [&>p+p]:mt-4 [&>ul+p]:mt-4">
                                                                <ReactMarkdown>
                                                                    {messageContent || ""}
                                                                </ReactMarkdown>
                                                            </div>

                                                            {messageContent?.startsWith(
                                                                "An error"
                                                            ) && (
                                                                <p
                                                                    className="text-[#2B71CC] text-sm cursor-pointer underline"
                                                                    onClick={() => {
                                                                        if (errorId) {
                                                                            reloadMessages(errorId);
                                                                        }
                                                                    }}
                                                                >
                                                                    {" "}
                                                                    Try again
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                        {Renderer && !expandedId && (
                                            <Renderer
                                                resultMessage={resultMessage}
                                                expandedId={expandedId}
                                                setExpandedId={setExpandedId}
                                                messageId={messageId}
                                                threadId={threadId}
                                                toolName={toolName}
                                            />
                                        )}
                                    </div>
                                );
                            }

                            if (messageRole === "user") {
                                const parts = messageContent?.split(/(\/.+?\s+SKU:[A-Za-z0-9-]+)/g);
                                const mediaItems = media?.filter(
                                    (media) => media?.messageId === messageId
                                );

                                return (
                                    <div className="mb-2" key={idx}>
                                        <div className={`flex items-center gap-2 justify-end`}>
                                            <div
                                                className={`w-fit ${
                                                    expandedId
                                                        ? "max-w-[100%]"
                                                        : mediaItems?.find((media) => media?.path)
                                                        ? "!min-w-[50%]"
                                                        : "max-w-[80%]"
                                                } rounded-xl p-3.5 text-sm
                                            bg-slate-100 text-slate-500 `}
                                            >
                                                <p className="">
                                                    {parts?.map((part, idx) =>
                                                        part.match(/\/.+?\s+SKU:[A-Za-z0-9-]+/) ? (
                                                            <span
                                                                key={idx}
                                                                className="!text-[#0054C2] !bg-[#E0EBF7] p-1 space-y-1"
                                                            >
                                                                {part}
                                                            </span>
                                                        ) : (
                                                            part
                                                        )
                                                    )}
                                                </p>

                                                <div
                                                    className={`flex flex-col gap-2 pt-1 space-y-1 ${
                                                        mediaItems?.length > 0 ? "pt-1" : ""
                                                    }`}
                                                >
                                                    {mediaItems?.map((mediaItem, idx) => {
                                                        return (
                                                            <>
                                                                {mediaItem?.path ? (
                                                                    <div key={idx} className="">
                                                                        <div className="bg-white rounded-[6px] p-2 border border-slate-200">
                                                                            <div className="flex items-center justify-between gap-10">
                                                                                <div className="flex w-full space-x-3 items-center">
                                                                                    {displayFileIcon(
                                                                                        mediaItem?.fileName
                                                                                            ?.split(
                                                                                                "."
                                                                                            )
                                                                                            .pop()
                                                                                            ?.toLowerCase() ||
                                                                                            "",
                                                                                        true,
                                                                                        "w-[25px] h-[25px]"
                                                                                    )}
                                                                                    <div>
                                                                                        <p className="text-sm font-rocGroteskMedium text-gm-50">
                                                                                            {truncate(
                                                                                                mediaItem?.fileName,
                                                                                                {
                                                                                                    length: 30,
                                                                                                }
                                                                                            )}
                                                                                        </p>
                                                                                        <div className="flex items-center gap-2">
                                                                                            {mediaItem?.size && (
                                                                                                <p className="text-sm font-rocGroteskMedium text-slate-500">
                                                                                                    {convertBytes(
                                                                                                        Number(
                                                                                                            mediaItem?.size ??
                                                                                                                0
                                                                                                        )
                                                                                                    )}
                                                                                                </p>
                                                                                            )}

                                                                                            {mediaItem?.size && (
                                                                                                <p className="h-1 w-1 bg-slate-500 rounded-full"></p>
                                                                                            )}
                                                                                            <p className="text-sm font-rocGroteskMedium text-[#0AAD0A]">
                                                                                                File
                                                                                                uploaded
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div className="flex items-center gap-2">
                                                                                    <div
                                                                                        className="border border-n-20 rounded-[4px] p-2 cursor-pointer"
                                                                                        onClick={() => {
                                                                                            const a =
                                                                                                document.createElement(
                                                                                                    "a"
                                                                                                );
                                                                                            a.href =
                                                                                                mediaItem?.path;
                                                                                            a.target =
                                                                                                "_blank";
                                                                                            a.download =
                                                                                                mediaItem?.fileName;
                                                                                            a.click();
                                                                                        }}
                                                                                    >
                                                                                        <i className="ri-external-link-line text-gm-50"></i>
                                                                                    </div>

                                                                                    {/* <div
                                                                                        className="border border-n-20 rounded-[4px] p-2 cursor-pointer"
                                                                                        onClick={() => {
                                                                                            window.open(
                                                                                                mediaItem?.path,
                                                                                                "_blank"
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <i className="ri-download-line text-gm-50 "></i>
                                                                                    </div> */}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <></>
                                                                )}
                                                            </>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                        })}

                        {isLoading && (
                            <div className="flex items-center gap-2 justify-start">
                                <SalesTargetThinkingState messages={messagesAfterLastUser} />
                            </div>
                        )}
                        {interrupt && <div>{interrupt}</div>}
                    </div>
                    {messages?.length === 0 && pageType !== "flow" && (
                        <div className="">
                            <p className="text-sm text-slate-500">Try these examples:</p>
                            {/* <MyComponent /> */}
                            <div className="mt-2 ">
                                <div className="flex items-center min-w-0 space-x-2 overflow-x-auto w-[100%] cursor-pointer">
                                    {[
                                        "I have a sales target of 6000 units over the next...",
                                        "Generate a forecast for 8000 units over the next 4 months... ",
                                        "Can you recalculate if I increase...",
                                    ]?.map((item, idx) => {
                                        return (
                                            <span
                                                className="text-sm text-slate-500 bg-slate-100 rounded-lg p-2 w-fit flex-shrink-0"
                                                key={idx}
                                                onClick={() => {
                                                    setMessage(item?.split("...")[0]);
                                                }}
                                            >
                                                {item}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="">
                    {(uploadingDoc || filesAttached?.length > 0) && (
                        <div className="flex flex-nowrap overflow-x-auto items-center space-x-2 py-2">
                            {loadingAttachName && uploadingDoc && (
                                <div className="mb-2 items-center px-3 py-2  rounded-[6px] space-x-3 border border-slate-100  max-width-[250px] justify-between  opacity-50 inline-flex">
                                    <div className="flex items-center gap-2">
                                        <div className="bg-n-20 rounded-[6px] w-[25px] h-[25px] flex items-center justify-center">
                                            <Loader size={4} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-rocGroteskMedium text[#64748B]">
                                                {truncate(loadingAttachName, {
                                                    length: 25,
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {filesAttached?.length > 0 &&
                                filesAttached?.map((doc, idx) => (
                                    <div className="inline-flex">
                                        <div className="flex space-x-1 w-full items-center">
                                            <div
                                                className="flex items-center px-2.5 py-[7px]  rounded-xl space-x-3 border border-slate-200 flex-shrink-0 w-[210px] justify-between bg-[#fff] 
                                        shadow-[0px_4px_12px_-2px_rgba(212,212,212,0.03),0px_10px_15px_-4px_rgba(212,212,212,0.01),0px_8px_6px_0px_rgba(212,212,212,0.04)]"
                                            >
                                                <div className="flex items-center gap-2">
                                                    {displayFileIcon(
                                                        doc?.fileName
                                                            ?.split(".")
                                                            .pop()
                                                            ?.toLowerCase() || "",
                                                        true,
                                                        "w-[25px] h-[25px]"
                                                    )}
                                                    <div>
                                                        <p
                                                            className="text-xs font-rocGroteskMedium text-gm-50 cursor-pointer"
                                                            onClick={() => {
                                                                const a =
                                                                    document.createElement("a");
                                                                a.href = doc?.path;
                                                                a.target = "_blank";
                                                                document.body.appendChild(a);
                                                                a.click();
                                                                document.body.removeChild(a);
                                                            }}
                                                        >
                                                            {truncate(doc?.fileName, {
                                                                length: 20,
                                                            })}
                                                        </p>
                                                        <p className="text-xs font-rocGroteskMedium text-slate-500">
                                                            {convertBytes(Number(doc?.size ?? 0))}
                                                        </p>
                                                    </div>
                                                </div>
                                                <i
                                                    className="ri-close-fill text-gm-50 cursor-pointer"
                                                    onClick={() => deleteAttachment(idx)}
                                                ></i>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                    <div
                        className=" border border-slate-200 rounded-lg px mt-2
                            shadow-[0px_4px_12px_-2px_rgba(212,212,212,0.03),0px_10px_15px_-4px_rgba(212,212,212,0.01),0px_8px_6px_0px_rgba(212,212,212,0.04)]"
                    >
                        <div className="relative" id="textAreaInput">
                            <CustomEditor
                                message={message}
                                setMessage={setMessage}
                                setSelectedIds={setSelectedIds}
                                selectedIds={selectedIds}
                                currentSelectedIds={currentSelectedIds}
                                setCurrentSelectedIds={setCurrentSelectedIds}
                                sendMessage={handleSendMessage}
                                hasPreviousMessage={messages?.length > 0}
                                threadId={threadId}
                                isLoading={isLoading}
                                setStopModal={setStopModal}
                                handleUpload={handleUpload}
                                setLoadingAttachName={setLoadingAttachName}
                                uploadingDoc={uploadingDoc}
                            />
                        </div>
                    </div>
                    {!expandedId && (
                        <p className="text-center text-[#64748B] text-xs pt-2 font-rocGroteskRegular">
                            Synth can make mistakes. Please double-check responses.
                        </p>
                    )}
                </div>
            </div>
        );
    };

    const CanvasRender = () => {
        const Renderer = toolRenderers[canvasRenderData?.toolName];
        return (
            <div className="p-6 space-y-2">
                <div className=" overflow-y-auto h-[calc(100vh-280px)] overflow-x-clip space-y-2 flex flex-col justify-between">
                    <div className="mb-2">
                        {Renderer && (
                            <Renderer
                                resultMessage={canvasRenderData?.resultMessage}
                                expandedId={expandedId}
                                setExpandedId={setExpandedId}
                                messageId={expandedId}
                                threadId={threadId}
                                toolName={canvasRenderData?.toolName}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="flex gap-2 relative justify-center !items-stretch ">
                {previousChatLayout && (
                    <ChatHistory
                        previousChatLayout={previousChatLayout}
                        type={pageType}
                        expandedId={expandedId}
                        reset={reset}
                        threadId={threadId}
                        setThreadId={setThreadId}
                        setCurrentChat={setCurrentChat}
                        contentMessage={contentMessage}
                        setContentMessage={setContentMessage}
                        setExpandedId={setExpandedId}
                        setMessage={setMessage}
                        setMessages={setMessages}
                        messages={messages}
                        sendMessage={sendMessage}
                        handleCreateConversation={handleCreateConversation}
                        Messages={Messages}
                    />
                )}

                <div
                    className={`bg-white shadow-card Shadow relative rounded-lg font-rocGroteskMedium ${
                        pageType === "flow" && "border border-slate-100"
                    }`}
                    style={{
                        width: `calc(100% - ${
                            previousChatLayout && pageType === "flow"
                                ? "387px"
                                : previousChatLayout
                                ? "250px"
                                : "0px"
                        })`,
                    }}
                >
                    <div className="px-6 w-full flex py-3 border-b border-slate-200 text-center items-center justify-between ">
                        <div className="flex items-center gap-2 ">
                            <i
                                className={` text-g-75 text-sm cursor-pointer transition-all duration-300 ${
                                    previousChatLayout
                                        ? "ri-layout-right-2-line"
                                        : "ri-layout-left-2-line"
                                }`}
                                onClick={() => {
                                    setPreviousChatLayout(!previousChatLayout);
                                }}
                            ></i>
                            {pageType === "flow" ? (
                                <p className="text-base font-rocGroteskMedium text-g-75">
                                    {/* {expandedId ? "Chat" : "Chat history"} */}
                                    Chat
                                </p>
                            ) : (
                                <p className="text-base font-rocGroteskMedium bg-gradient-to-r from-[#56B648] to-[#FFB902] bg-clip-text text-transparent">
                                    {truncate(currentChat, { length: 50 }) || "New sales target"}
                                </p>
                            )}
                        </div>

                        {expandedId ? (
                            <Button
                                btnText="Collapse Canvas"
                                btnClassname="!w-fit !text-g-75 !border-slate-200 !py-2.5 !px-4 !rounded-[20px] "
                                icon={<i className="ri-picture-in-picture-exit-line text-g-75"></i>}
                                onClick={() => {
                                    setExpandedId("");
                                }}
                            />
                        ) : (
                            <div className="flex items-center gap-3">
                                {pageType !== "flow" ? (
                                    <i
                                        className="ri-close-fill text-slate-500 text-lg cursor-pointer"
                                        onClick={() => {
                                            setOpen(false);
                                            updateSearchParams({
                                                updated: "true",
                                            });
                                        }}
                                    ></i>
                                ) : (
                                    <div
                                        className="border border-slate-200 rounded-[4px] px-4 py-2 text-sm text-gm-50 cursor-pointer flex items-center gap-2"
                                        onClick={() => {
                                            navigator.clipboard.writeText(window.location.href);
                                            setCopied(true);
                                        }}
                                    >
                                        {copied && (
                                            <i className="ri-checkbox-circle-fill text-[#0AAD0A] text-sm"></i>
                                        )}
                                        {copied ? "Link copied" : "Copy Link"}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {expandedId ? CanvasRender() : <div className={`mx-auto `}>{Messages()}</div>}
                </div>
            </div>

            <ModalContainer
                open={stopModal}
                modalClassName={"py-10 px-10"}
                tailwindClassName="md:w-[500px] w-[90%] "
                showCloseIcon={false}
                closeModal={() => {
                    setStopModal(false);
                }}
            >
                <div className="bg-white px-6 py-4  shadow-cardShadow  rounded-[8px]">
                    <div className="flex items-center justify-between">
                        <h1 className="text-base mb-1 text-[#142837] font-rocGroteskMedium">
                            Are you sure you want to stop this?
                        </h1>
                        <i
                            className="ri-close-line text-g-75 text-lg cursor-pointer -mt-2"
                            onClick={() => {
                                setStopModal(false);
                            }}
                        ></i>
                    </div>
                    <p className="text-slate-500 font-rocGroteskMedium text-sm">
                        The AI agent is still working on your request. Stopping now will cancel the
                        process and no results will be generated.
                    </p>
                    <div className="flex justify-end items-center mt-6">
                        <div className="flex items-center !whitespace-nowrap space-x-2  ">
                            <Button
                                btnText={"Continue"}
                                type={"button"}
                                btnClassname={"!bg-n-20 !w-fit !text-gm-50"}
                                onClick={() => {
                                    setStopModal(false);
                                }}
                            />
                            <Button
                                btnClassname={"!py-3 !w-fit !bg-[#E4281D] !text-white"}
                                onClick={() => {
                                    stopGeneration();
                                    setStopModal(false);
                                }}
                                btnText={"Yes, stop"}
                                type={"button"}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={cantHandleTaskModal}
                modalClassName={"py-10 px-10 !justify-end !items-start "}
                tailwindClassName="max-w-[408px] "
                showCloseIcon={false}
                closeModal={() => {
                    setCantHandleTaskModal(false);
                }}
            >
                <div className="bg-white p-3  shadow-cardShadow  rounded-[6px] border border-slate-200">
                    <div className="flex gap-2">
                        <i className="ri-information-fill text-[#ECAA01] text-sm"></i>

                        <div>
                            <div className="flex items-center justify-between">
                                <h1 className="text-base mb-1 text-g-75 font-rocGroteskMedium">
                                    Synth can't handle this task (yet)
                                </h1>
                                <i
                                    className="ri-close-line text-slate-500 text-sm cursor-pointer -mt-2"
                                    onClick={() => {
                                        setStopModal(false);
                                    }}
                                ></i>
                            </div>
                            <p className="text-slate-500 font-rocGroteskMedium text-sm">
                                This request isn’t supported right now because it’s outside Synth’s
                                capabilities. You can assign it to someone else on your team. In the
                                meantime, would you like me to share this task with our product team
                                so we can improve?
                            </p>
                            <div className="flex justify-end items-center mt-2.5">
                                <div className="flex items-center !whitespace-nowrap space-x-2  ">
                                    <Button
                                        btnText={"No, close"}
                                        type={"button"}
                                        btnClassname={"!py-2 !bg-n-20 !w-fit !text-gm-50"}
                                        onClick={() => {
                                            setCantHandleTaskModal(false);
                                            toast.custom((t) => (
                                                <CustomToast
                                                    t={t}
                                                    title={"Got it"}
                                                    message={
                                                        "I won't share this task info with Synth with the product team."
                                                    }
                                                    type={"success"}
                                                />
                                            ));
                                        }}
                                    />
                                    <Button
                                        btnClassname={"!py-2 !w-fit "}
                                        onClick={() => {
                                            setCantHandleTaskModal(false);
                                            setAdditionalInfoModal(true);
                                        }}
                                        btnText={"Yes, share"}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ModalContainer>

            <ModalContainer
                open={additionalInfoModal}
                modalClassName={"py-10 px-10 !justify-end !items-start "}
                tailwindClassName="w-[408px] max-sm:w-[80%] "
                showCloseIcon={false}
                closeModal={() => {
                    setAdditionalInfoModal(false);
                }}
            >
                <div className="bg-white p-3  shadow-cardShadow  rounded-[6px] border border-slate-200">
                    <div>
                        <div className="flex items-center justify-between">
                            <h1 className="text-sm mb-1 text-g-75 font-rocGroteskMedium">
                                Additional context
                                <span className="text-slate-400 text-sm"> (optional)</span>
                            </h1>
                            <i
                                className="ri-close-line text-slate-500 text-sm cursor-pointer -mt-2"
                                onClick={() => {
                                    setAdditionalInfoModal(false);
                                }}
                            ></i>
                        </div>
                        <textarea
                            required={true}
                            name="userFeedbackMsg"
                            value={userFeedbackMsg}
                            onChange={(e) => setUserFeedbackMsg(e.target.value)}
                            className="min-h-[136px] font-rocGroteskMedium w-full h-24 border border-slate-200 rounded-md p-2 placeholder:text-slate-500 placeholder:text-[13px] text-[13px] resize-none outline-none"
                            placeholder="Share more details about your feedback… (e.g., what you were trying to do, any errors, or suggestions)"
                        />
                        <div className="flex justify-end items-center mt-2.5">
                            <div className="flex items-center !whitespace-nowrap space-x-2  ">
                                <Button
                                    btnText={"No, skip"}
                                    type={"button"}
                                    btnClassname={"!py-2 !bg-n-20 !w-fit !text-gm-50"}
                                    onClick={() => {
                                        handleSendFlowFeedback();
                                    }}
                                />
                                <Button
                                    btnClassname={"!py-2 !w-fit"}
                                    onClick={() => {
                                        handleSendFlowFeedback();
                                    }}
                                    btnText={"Submit"}
                                    isLoading={sendingFlowFeedback}
                                    disabled={sendingFlowFeedback}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </ModalContainer>
        </div>
    );
};

export default ChatUIComponent;
